import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, switchMap } from 'rxjs';
import { TutorTrainingCompletion, UserObj } from '../microservice-clients/user';
import { AcademyCourse } from '../models/academy-course.model';
import { academyCourses } from '../standalone-components/tutor-academy/academy-courses.constants';
import { AuthService } from './auth.service';
import { StatsigAppService } from './statsig.service';
import { UtilityService } from './utility.service';

@Injectable({
  providedIn: 'root'
})
export class TutorAcademyService {

  constructor(
    private authService: AuthService,
    private httpClient: HttpClient,
    private statsigApp: StatsigAppService,
    private utilityService: UtilityService,
  ) { }

  getCourses(): Array<AcademyCourse> {
    const completed = this.readCompletedModules();
    return this.visibleCourses().map(course => this.withCompletion(course, completed));
  }

  getOptionalCourses(): Array<AcademyCourse> {
    return this.getCourses().filter(course => !course.isMandatory);
  }

  getCourse(slug: string): AcademyCourse | null {
    return this.getCourses().find(course => course.slug === slug) ?? null;
  }

  getMandatoryCourse(): AcademyCourse | undefined {
    return this.getCourses().find(course => course.isMandatory);
  }

  isMandatoryCourseCompleted(): boolean {
    const mandatoryCourse = this.getMandatoryCourse();
    return mandatoryCourse ? !!mandatoryCourse.isCompleted : true;
  }

  startCourse(slug: string): void {
    this.statsigApp.logEvent('academy_course_start', undefined, { slug });
  }

  trackLessonComplete(slug: string, lessonNumber: number): void {
    this.statsigApp.logEvent('academy_lesson_complete', lessonNumber, { slug });
  }

  completeCourse(slug: string): Observable<UserObj | null> {
    const course = this.visibleCourses().find(item => item.slug === slug);
    if (!course) {
      return of(null);
    }

    this.statsigApp.logEvent('academy_course_complete', undefined, { slug, isMandatory: course.isMandatory });

    const tutorId = this.authService.userDetails?.id;
    if (!course.trainingModuleId || !tutorId) {
      return of(null);
    }

    const route = `${this.utilityService.apiBase}/user/tutor/${tutorId}/training/${course.trainingModuleId}/complete`;

    return this.httpClient.post(route, {}).pipe(
      switchMap(() => this.authService.getUserDetails())
    );
  }

  private visibleCourses(): Array<AcademyCourse> {
    return academyCourses.filter(course => !course.isGermanOnly || this.utilityService.isDE);
  }

  private readCompletedModules(): Array<TutorTrainingCompletion> {
    return this.authService.userDetails?.tutorDetails?.training?.completed ?? [];
  }

  private withCompletion(course: AcademyCourse, completed: Array<TutorTrainingCompletion>): AcademyCourse {
    const completion = course.trainingModuleId
      ? completed.find(entry => entry.moduleId === course.trainingModuleId)
      : undefined;

    return {
      ...course,
      isCompleted: !!completion,
      completedAt: completion?.completedAt ?? null
    };
  }
}
